// =============================================================================
// clockPinCheck.js — the pure decisions behind the clock-in / clock-out PIN step
// on a server-PIN-verify host (Lane BT).
//
// On a gated host (SERVER_PIN_VERIFY_HOSTS in auth.js — empty today) the clock
// scrim asks the server "is this PIN real, and whose is it?" instead of reading
// the downloaded roster. The login numpad already does three things while it
// waits for that kind of answer, and the clock scrim did not:
//   1. show a neutral "Checking…" instead of nothing;
//   2. ignore an answer that lands AFTER the person retyped or closed the scrim
//      (an attempt counter — a stale answer must never move the flow on);
//   3. give up after a timeout and hand the numpad back with the existing
//      "couldn't reach" message, instead of leaving the person staring at a
//      dead numpad on slow mall wifi.
//
// Everything here is pure (no DOM, no network, no PIN value — only the SHAPE of
// the server's reply) so it is unit-tested in clockPinCheck.test.mjs. The OFF
// path (a host NOT in the gate) never runs a check: on that path the flow object
// never gains a `checking` field, numpadBusy() hands back the flow's own `busy`
// value untouched, and showChecking() is false.
// =============================================================================

// How long the scrim waits for the server before handing the numpad back.
export const CLOCK_PIN_VERIFY_TIMEOUT_MS = 8000;

// The message when the login service could not be reached (offline / timed
// out). Same wording verifyPin itself returns, so the two can never drift.
export const UNREACHABLE_MESSAGE = "Couldn't reach the login service.";

// Decide what the scrim does with a server reply.
//   srv      — what pinAuth.verifyPin resolved to (or null if it threw)
//   attempt  — the attempt number this check was started with
//   current  — the newest attempt number (bumped on every 4th digit, and on
//              scrim close / unmount)
// Returns exactly one of:
//   { stale: true }                 — retyped / closed meanwhile: touch NOTHING
//   { unreachable: true, error }    — offline or timed out: show `error`, clear
//                                     the PIN, re-enable the numpad
//   { name: "<verified name>" }     — the server said yes
//   { name: null }                  — the server said no (the caller shows the
//                                     same opaque message it always did)
export function clockPinOutcome(srv, attempt, current) {
  if (attempt !== current) return { stale: true };
  if (srv && srv.ok) return { name: srv.staffName || null };
  if (srv && srv.unreachable) return { unreachable: true, error: srv.error || UNREACHABLE_MESSAGE };
  return { name: null };
}

// Is the scrim numpad disabled? While a server check is running, yes. When no
// check is running — and on the OFF path, where `checking` is never set — this
// is the flow's own `busy` flag, the exact value the numpad received before.
export function numpadBusy(flow) {
  return flow.checking ? true : flow.busy;
}

// Should the neutral "Checking…" line show? Only while a check is running and
// no error is on screen. Never on the OFF path (`checking` is never set there).
export function showChecking(flow) {
  return !!flow.checking && !flow.err;
}
